<script setup lang="ts">
  import { computed, ref } from 'vue';

  import type { CommunicationChannel, Contact, Partner } from '@stuut/api/models';
  import { ConfirmDialog } from '@ui/components/confirm-dialog';
  import { toast } from '@ui/components/sonner';

  import { stuut } from '@/api';
  import { ContactEditorDialog } from '@/components/contacts';
  import AccountContactEditorDialog from '@/components/contacts/AccountContactEditorDialog.vue';
  import ContactCard from '@/components/contacts/ContactCard.vue';

  interface Props {
    contacts?: Contact[];
    disableDropdown?: boolean;
    mode?: 'external' | 'internal';
    partner: Partner;
  }

  const props = withDefaults(defineProps<Props>(), {
    mode: 'external',
  });

  const emits = defineEmits<{
    (e: 'afterSave', contact: Contact): void;
  }>();

  const confirmDeleteDialogOpen = ref<boolean>(false);
  const contactEditorOpen = ref<boolean>(false);
  const selectedContact = ref<Contact | null>(null);

  const allContacts = computed<Contact[]>(() => props.contacts || props.partner.allContacts);

  const expandedContact = ref<Contact | null>(null);

  const toggleContact = (contact: Contact) => {
    if (expandedContact.value?.id === contact.id) {
      expandedContact.value = null;
    } else {
      expandedContact.value = contact;
    }
  };

  const makePrimary = (contact: Contact, communicationChannel: CommunicationChannel) => {
    return stuut.partners
      .setPrimary(props.partner, contact, communicationChannel)
      .catch((err) => toast.error(err.clientMessage));
  };

  const enableContact = (
    contact: Contact,
    communicationChannel: CommunicationChannel,
    eventSource?: string,
  ) => {
    let _contact = contact;
    if (contact.id.startsWith('ptn_')) {
      _contact = props.partner.contacts.find((ctc) => ctc.isPartnerAccountContact) as Contact;
    }

    return stuut.partners
      .enable(props.partner, _contact, communicationChannel, eventSource)
      .catch((err) => {
        toast.error(err.clientMessage);
      });
  };

  const disableContact = (
    contact: Contact,
    communicationChannel: CommunicationChannel,
    eventSource?: string,
  ) => {
    let _contact = contact;
    if (contact.id.startsWith('ptn_')) {
      _contact = props.partner.contacts.find((ctc) => ctc.isPartnerAccountContact) as Contact;
    }

    return stuut.partners
      .disable(props.partner, _contact, communicationChannel, eventSource)
      .catch((err) => {
        toast.error(err.clientMessage);
      });
  };

  const deleteContact = (contact: Contact | null) => {
    if (selectedContact.value?.id === contact?.id) {
      selectedContact.value = null;
    }
    if (contact) {
      stuut.partners
        .deleteContact(props.partner, contact)
        .catch((err) => toast.error(err.clientMessage));
    }
  };

  const closeContactEditor = () => {
    selectedContact.value = null;
    contactEditorOpen.value = false;
  };

  const savedAccountContactEditor = (partner: Partner) => {
    if (selectedContact.value) {
      selectedContact.value._name = partner.name;
      selectedContact.value.email = partner.email ?? '';
      selectedContact.value.phone = partner.phone;
    }

    closeContactEditor();
  };

  const savedContactEditor = (newContact: Contact) => {
    if (newContact) {
      emits('afterSave', newContact);
    }

    closeContactEditor();
  };

  defineExpose({
    openContactEditor: () => {
      contactEditorOpen.value = true;
    },
  });
</script>

<template>
  <div v-if="allContacts.length" class="flex flex-col w-full gap-y-4">
    <ContactCard
      v-for="contact in allContacts"
      :key="contact?.id"
      :contact="contact"
      :partner="props.partner"
      expandable
      :expanded="contact?.id === expandedContact?.id"
      class="w-full"
      :disable-dropdown="props.disableDropdown"
      @toggle="toggleContact(contact)"
      @make-primary="(contact, channel) => makePrimary(contact, channel as CommunicationChannel)"
      @enable-contact="
        (contact, channel, event_source?) =>
          enableContact(contact, channel as CommunicationChannel, event_source)
      "
      @disable-contact="
        (contact, channel, event_source?) =>
          disableContact(contact, channel as CommunicationChannel, event_source)
      "
      @edit="
        () => {
          selectedContact = contact;
          contactEditorOpen = true;
        }
      "
      @delete="
        () => {
          selectedContact = contact;
          confirmDeleteDialogOpen = true;
        }
      "
    />
  </div>

  <div v-else class="w-full text-center text-muted-foreground">No contacts yet</div>

  <ConfirmDialog
    v-model="confirmDeleteDialogOpen"
    confirm-text="Delete Contact"
    @confirm="deleteContact(selectedContact)"
    @close="
      () => {
        selectedContact = null;
      }
    "
  >
    <template #title> Are you sure? </template>

    <template #description> This will permanently delete this contact. </template>
  </ConfirmDialog>

  <AccountContactEditorDialog
    v-if="contactEditorOpen && selectedContact && selectedContact?.isPartnerAccountContact"
    v-model="contactEditorOpen"
    :partner="props.partner"
    @saved="savedAccountContactEditor"
    @close="closeContactEditor"
  />

  <ContactEditorDialog
    v-else
    v-model="contactEditorOpen"
    :partner="props.partner"
    :mode="props.mode"
    :contact="selectedContact"
    @saved="savedContactEditor"
    @close="closeContactEditor"
  />
</template>

<style scoped>
  [style*='--before-bg']::before {
    background-image: var(--before-bg);
    background-size: 100% auto;
    background-repeat: no-repeat;
  }
</style>
